import { useRef, useState } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { ParcelQueryParams } from "../query.types";
import { ParcelResponseDTO } from "../response.types";
import { ParcelSearchCardModel } from "entities/ParcelSearchCard/model/model";
import { PaymentsModalType, SubscriptionDataModel } from "entities/shared/subscriptionModel";
import { OnErrorCallback, OnSettledCallback, OnSuccessCallback, useRequestCallbacks } from "shared/api/lib/api.hooks";
import { useAppDispatch } from "shared/lib/redux";
import { PartialBy } from "shared/lib/typeCheck.lib";

import { parcelSearchService } from "../api";

type RequestLimitReachedCallbackFunc = () => void;
type SetSearchCacheFunc = (data: ParcelResponseDTO | undefined) => void;
type ResetSearchQueryFunc = () => Promise<void>;
type OnSuccessCallbackFunc = OnSuccessCallback<ParcelSearchCardModel>;

type SearchRequestFuncOptions = {
  onSuccess?: OnSuccessCallbackFunc;
  onError?: OnErrorCallback<SearchError>;
  onSettled?: OnSettledCallback<ParcelSearchCardModel, SearchError>;
  resetQueryBeforeRequest?: boolean;
  onAppIntegrationRequestLimitReached?: RequestLimitReachedCallbackFunc;
  onWebAppRequestLimitReached?: RequestLimitReachedCallbackFunc;
};

export type ParcelSearchRequestFunc = (params: ParcelQueryParams, options?: SearchRequestFuncOptions) => void;

export type UseSearchQueryReturnType = {
  searchRequest: ParcelSearchRequestFunc;
  resetSearchQuery: ResetSearchQueryFunc;
  removeSearchCache: () => void;
  setSearchCache: SetSearchCacheFunc;
  cancelSearchRequest: () => Promise<void>;
  searchResponse: ParcelSearchCardModel | undefined;
  isSearchSuccess: boolean;
  isSearchError: boolean;
  isSearchLoading: boolean;
  isSearchFetching: boolean;
};

type SearchError = Error;
type QueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey"> & {
  isAuthenticated: boolean;
  setSubscription: (subscription: SubscriptionDataModel) => {
    type: string;
    payload: SubscriptionDataModel | null;
  };
  setActivePaymentModal: (activePaymentModal: PaymentsModalType) => {
    type: string;
    payload: PaymentsModalType | null;
  };
  setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached: boolean) => {
    type: string;
    payload: boolean | null;
  };
  setWebAppRequestLimitReached: (webAppRequestLimitReached: boolean) => {
    type: string;
    payload: boolean | null;
  };
};

const ROAD_SEARCH_CACHE_KEY = "search-parcel-tracking-query";

export const useSearchQuery = ({
  queryKey = [ROAD_SEARCH_CACHE_KEY],
  isAuthenticated,
  setSubscription,
  setActivePaymentModal,
  setAppIntegrationRequestLimitReached,
  setWebAppRequestLimitReached,
}: QueryParams): UseSearchQueryReturnType => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useState<ParcelQueryParams>();
  const [enabled, setEnabled] = useState(false);

  const appIntegrationRequestLimitReachedCallbackRef = useRef<RequestLimitReachedCallbackFunc | undefined>();
  const webAppRequestLimitReachedCallbackRef = useRef<RequestLimitReachedCallbackFunc | undefined>();

  const meta = useQuery<ParcelResponseDTO, SearchError, ParcelSearchCardModel>({
    queryKey,
    queryFn: searchParams ? ({ signal }) => parcelSearchService.search(searchParams, signal) : undefined,
    select: (data) => new ParcelSearchCardModel(data),
    enabled,
  });

  const { data, isError, isLoading, isSuccess, isFetching } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<ParcelSearchCardModel, SearchError>(meta);

  const removeSearchCache = () => {
    queryClient.removeQueries({ queryKey });
  };

  const resetSearchQuery: ResetSearchQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const setSearchCache: SetSearchCacheFunc = (data) => {
    queryClient.setQueryData(queryKey, data);
  };

  const searchRequest: ParcelSearchRequestFunc = (
    params,
    {
      onSuccess,
      onError,
      onSettled,
      resetQueryBeforeRequest = true,
      onAppIntegrationRequestLimitReached,
      onWebAppRequestLimitReached,
    } = {}
  ) => {
    setRequestCallbacks({
      onSuccess,
      onError,
      onSettled: (response, error) => {
        // TODO: Implement DataLayersManager
        // const dlm = new DataLayersManager();
        const subscription = response?.subscription;
        if (subscription) {
          dispatch(setSubscription(subscription));
        }

        if (response?.requestLimitReachedAppIntegration) {
          dispatch(setAppIntegrationRequestLimitReached(true));
          appIntegrationRequestLimitReachedCallbackRef.current?.();
        }
        if (response?.requestLimitReachedWebApp) {
          if (isAuthenticated) {
            if (subscription?.isFree) {
              dispatch(setActivePaymentModal(PaymentsModalType.FREE));
              // dlm.setModel(response).searchLimitReached();
            } else {
              dispatch(setActivePaymentModal(PaymentsModalType.PAY));
            }
          } else {
            dispatch(setWebAppRequestLimitReached(true));
          }
          webAppRequestLimitReachedCallbackRef.current?.();
        }
        onSettled?.(response, error);
        setEnabled(false);
      },
    });

    appIntegrationRequestLimitReachedCallbackRef.current = onAppIntegrationRequestLimitReached;
    webAppRequestLimitReachedCallbackRef.current = onWebAppRequestLimitReached;

    if (resetQueryBeforeRequest) {
      resetSearchQuery().finally(() => {
        setSearchParams(params);
        setEnabled(true);
      });
      return;
    }

    setSearchParams(params);
    setEnabled(true);
  };

  const cancelSearchRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  return {
    searchRequest,
    resetSearchQuery,
    removeSearchCache,
    setSearchCache,
    searchResponse: data,
    isSearchSuccess: isSuccess,
    isSearchError: isError,
    isSearchLoading: isLoading,
    isSearchFetching: isFetching,
    cancelSearchRequest,
  };
};
